import React, { useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { CreditCard, RefreshCw, AlertTriangle } from "lucide-react";

const TYPE_LABELS = {
  customer: "Customers",
  connect_account: "Connect accounts",
  subscription: "Subscriptions",
  identity_session: "Identity sessions",
};

const INV_LABELS = {
  order_checkout_sessions: "Order checkout sessions",
  order_payment_intents: "Order payment intents",
  order_charges: "Order charges",
  order_transfers: "Order transfers",
  order_refunds: "Order refunds",
  credit_checkout_sessions: "Credit checkout sessions",
};

// Read-only Stripe object audit — counts stored Stripe IDs and, when the key is valid, checks which
// still resolve against the current account. Lives in Admin → Platform Sitrep alongside the other
// system health checks (Backend/API, Database, EasyPost, …). Never writes/deletes an ID.
export default function StripeObjectAudit() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/stripe/id-audit");
      setData(res.data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Audit failed.");
    } finally { setBusy(false); }
  };

  const keyTone = {
    valid: "text-olive-bright", invalid: "text-[#e07a5f]",
    error: "text-amber-400", no_key: "text-muted-foreground",
  }[data?.key_status] || "text-muted-foreground";

  const s = data?.summary || {};
  const hasOrphans = (s.total_orphaned || 0) > 0;

  return (
    <div className="mt-6 border border-sniper bg-surface p-5" data-testid="stripe-id-audit">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-heading text-lg uppercase tracking-tight text-foreground flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-olive-bright" /> Stripe Object Audit
          </h3>
          <p className="text-[11px] font-mono uppercase text-muted-foreground mt-1 max-w-xl">
            Read-only. Counts every stored Stripe ID and, when the key is valid, checks which still resolve
            against the current account. Never writes, deletes, or clears an ID.
          </p>
        </div>
        <button onClick={run} disabled={busy} data-testid="stripe-audit-run"
          className="inline-flex items-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase px-4 py-2 text-sm transition-colors">
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {busy ? "Auditing…" : (data ? "Re-run" : "Run audit")}
        </button>
      </div>

      {data && (
        <div className="mt-4 space-y-4" data-testid="stripe-audit-results">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 font-mono text-[11px] uppercase">
            <span className="text-muted-foreground">Key: <span className={keyTone} data-testid="stripe-audit-key-status">{data.key_status}</span></span>
            <span className="text-muted-foreground">Mode: <span className="text-secondary-foreground">{data.stripe_mode}</span></span>
            <span className="text-muted-foreground">Account: <span className="text-secondary-foreground">{data.account_id || "—"}</span></span>
          </div>

          {hasOrphans && (
            <div className="flex items-start gap-2 border border-[#e07a5f]/50 bg-[#e07a5f]/10 px-3 py-2" data-testid="stripe-audit-orphan-warning">
              <AlertTriangle className="w-4 h-4 text-[#e07a5f] mt-0.5 shrink-0" />
              <p className="font-mono text-[11px] uppercase text-[#e0a090] leading-relaxed">
                {s.total_orphaned} orphaned object(s) — belong to a different Stripe account. Plan a deliberate migration.
              </p>
            </div>
          )}

          <div className="border border-sniper" data-testid="stripe-audit-reusable">
            <div className="grid grid-cols-[1.4fr_repeat(4,1fr)] font-mono text-[10px] uppercase text-muted-foreground border-b border-sniper">
              <div className="px-3 py-2">Reusable type</div>
              <div className="px-3 py-2 text-right">Stored</div>
              <div className="px-3 py-2 text-right">Resolved</div>
              <div className="px-3 py-2 text-right">Orphaned</div>
              <div className="px-3 py-2 text-right">Unreachable</div>
            </div>
            {Object.entries(data.resolved_types || {}).map(([k, t]) => (
              <div key={k} data-testid={`stripe-audit-row-${k}`}
                className="grid grid-cols-[1.4fr_repeat(4,1fr)] font-mono text-[11px] border-b border-sniper/50 last:border-0">
                <div className="px-3 py-2 text-secondary-foreground uppercase">{TYPE_LABELS[k] || k}</div>
                <div className="px-3 py-2 text-right text-foreground tabular-nums">{t.stored}</div>
                <div className="px-3 py-2 text-right text-olive-bright tabular-nums">{data.resolved ? t.resolved : "—"}</div>
                <div className={`px-3 py-2 text-right tabular-nums ${t.orphaned ? "text-[#e07a5f]" : "text-muted-foreground"}`}>{data.resolved ? t.orphaned : "—"}</div>
                <div className={`px-3 py-2 text-right tabular-nums ${t.unreachable ? "text-amber-400" : "text-muted-foreground"}`}>{data.resolved ? t.unreachable : "—"}</div>
              </div>
            ))}
          </div>

          <div>
            <p className="font-mono text-[10px] uppercase text-muted-foreground mb-2">Inventory only (transactional — never re-called)</p>
            <div className="flex flex-wrap gap-x-6 gap-y-1">
              {Object.entries(data.inventory_only || {}).map(([k, v]) => (
                <span key={k} className="font-mono text-[11px] uppercase text-secondary-foreground" data-testid={`stripe-audit-inv-${k}`}>
                  {INV_LABELS[k] || k}: <span className="text-foreground tabular-nums">{v}</span>
                </span>
              ))}
            </div>
          </div>

          <p className="font-mono text-[10px] uppercase text-muted-foreground leading-relaxed border-t border-sniper pt-3" data-testid="stripe-audit-note">
            {data.note}
          </p>
        </div>
      )}
    </div>
  );
}
